"use client";

import { useEffect } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "@unreserved/hooks";
import type {
  AvatarDto,
  ChangePasswordResponseDto,
  EditProfileDto,
  UploadProfileImageResponseDto,
  UserProfileDto,
  UserProfileResponseDto,
} from "@unreserved/sdk";
import { userApi } from "@unreserved/services";
import { handleHttpError } from "@unreserved/utils/http-error";

import { useAuthStore } from "@/stores/auth-store";

export function useProfile() {
  const setUser = useAuthStore((state) => state.setUser);

  const query = useQuery<UserProfileResponseDto>({
    queryKey: ["user", "profile"],
    queryFn: () => userApi.userControllerGetProfileV1(),
  });

  useEffect(() => {
    if (query.data?.data && query.data.status === "Success") {
      setUser(query.data.data as UserProfileDto);
    }
  }, [query.data, setUser]);

  return {
    ...query,
    profile: (query.data?.data ?? null) as UserProfileDto | null,
    setUser: (user: UserProfileDto) => {
      setUser(user);
    },
  };
}

export function useEditProfile() {
  const { toast } = useToast();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation<UserProfileResponseDto, Error, EditProfileDto>({
    mutationKey: ["user", "edit-profile"],
    mutationFn: (editProfileDto) =>
      userApi.userControllerEditProfileV1({
        editProfileDto,
      }),
    onSuccess: (data) => {
      if (data.status === "Success" && data.data) {
        setUser(data.data as UserProfileDto);
      }
      toast.success(data.message ?? "Profile updated successfully.");
    },
    onError: (error) => {
      handleHttpError(error, "Failed to edit profile", toast);
    },
  });
}

export function useUploadProfileImage() {
  const { toast } = useToast();
  const setUser = useAuthStore((state) => state.setUser);
  const user = useAuthStore((state) => state.user);

  return useMutation<UploadProfileImageResponseDto, Error, File>({
    mutationKey: ["user", "upload-profile-image"],
    mutationFn: (file) =>
      userApi.userControllerUploadProfileImageV1({
        file,
      }),
    onSuccess: (data) => {
      if (data.status === "Success" && data.data && user) {
        setUser({
          ...user,
          avatar: {
            ...(user.avatar ?? {}),
            fileUrl: data.data.fileUrl as unknown as object,
          } as AvatarDto,
        });
      }
    },
    onError: (error) => {
      handleHttpError(error, "Failed to upload profile image", toast);
    },
  });
}

export function useChangePassword() {
  const { toast } = useToast();

  return useMutation<
    ChangePasswordResponseDto,
    Error,
    { newPassword: string; confirmPassword: string }
  >({
    mutationKey: ["user", "change-password"],
    mutationFn: (input) =>
      userApi.userControllerChangePasswordV1({
        changePasswordDto: {
          newPassword: input.newPassword,
          confirmNewPassword: input.confirmPassword,
        },
      }),
    onSuccess: (data) => {
      toast.success(data.message ?? "Password updated successfully.");
    },
    onError: (error) => {
      handleHttpError(error, "Failed to update password", toast);
    },
  });
}
